// src/contexts/AuthContext.tsx
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  getCurrentUser, 
  logout as apiLogout, 
  login as apiLogin,
  register as apiRegister,
  forgotPassword as apiForgotPassword,
  resetPassword as apiResetPassword,
  User, 
  AuthError,
  LoginRequest,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest
} from '../services/authApi';
import { clearUserAccessToken } from '../utils/tokenStorage';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const refreshUser = async (retryCount = 0): Promise<void> => {
    // Prevent multiple simultaneous refresh calls
    if (isRefreshing) {
      return;
    }

    try {
      setIsRefreshing(true);
      // Only set loading to true on initial load, not on retries
      if (retryCount === 0) {
        setLoading(true);
      }
      setError(null);
      // getCurrentUser now has built-in retry logic, so we don't need to retry here
      // But we'll still handle the case where it fails after all retries
      const userData = await getCurrentUser();
      setUser(userData);
      setLoading(false);
    } catch (err) {
      const authError = err as AuthError;
      if (authError.code === 'AUTH_REQUIRED') {
        // User is not authenticated - clear user state
        setUser(null);
        setLoading(false);
        
        // Auto-redirect to login page if not already on login/auth pages
        const currentPath = window.location.pathname;
        const isAuthPage = currentPath === '/login' || 
                          currentPath === '/register' || 
                          currentPath === '/forgot-password' || 
                          currentPath === '/reset-password' ||
                          currentPath.startsWith('/auth/');
        
        if (!isAuthPage && !isLoggingOut) {
          // Store the intended destination to redirect after login
          // Only store if it's not the root path (/) - root should always go to /main
          if (currentPath !== '/main' && currentPath !== '/') {
            sessionStorage.setItem('redirectAfterLogin', currentPath + window.location.search);
          }
          // Redirect to login page
          window.location.href = '/login';
        }
      } else {
        // Network or other error - getCurrentUser already retried, so just set error
        // Don't clear user state as it might be a temporary network issue
        setError(authError.message || 'Failed to load user. The server may be starting up. Please try again.');
        setLoading(false);
        console.error('Auth refresh failed after all retries:', err);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  // Initial load on mount - skip if we just logged out
  useEffect(() => {
    // Check if we just logged out (sessionStorage flag)
    const justLoggedOut = sessionStorage.getItem('justLoggedOut') === 'true';
    if (justLoggedOut) {
      // Clear the flag
      sessionStorage.removeItem('justLoggedOut');
      // Set user to null and loading to false to prevent auto-login
      setUser(null);
      setLoading(false);
      return;
    }
    refreshUser();
  }, []);

  // Periodic session validation (every 5 minutes) to catch expired sessions
  useEffect(() => {
    // Only set up interval if we have a user
    if (!user) {
      return;
    }

    const interval = setInterval(() => {
      // Only validate if we have a user and not currently refreshing
      if (user && !isRefreshing) {
        refreshUser(0).catch(err => {
          console.warn('Periodic auth validation failed:', err);
        });
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user, isRefreshing]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      setError(null);
      setLoading(true);
      // apiLogin now has built-in retry logic with exponential backoff
      const response = await apiLogin(credentials);
      setUser(response.user);
      setLoading(false);
      
      // Small delay to ensure state is updated before redirect
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Redirect to stored path or default to /main
      const storedPath = sessionStorage.getItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAfterLogin');
      const redirectTo = (storedPath && storedPath !== '/') ? storedPath : '/main';
      window.location.href = redirectTo;
    } catch (err) {
      const authError = err as AuthError;
      // Provide more helpful error messages for server wake-up issues
      let errorMessage = authError.message || 'Login failed';
      if (errorMessage.includes('timeout') || errorMessage.includes('Server error')) {
        errorMessage = 'The server is starting up. Please wait a moment and try again.';
      }
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  };

  const register = async (data: RegisterRequest): Promise<void> => {
    try {
      setError(null);
      setLoading(true);
      const response = await apiRegister(data);
      setUser(response.user);
      setLoading(false);
      
      // Redirect to login page after successful registration
      window.location.href = '/login';
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message || 'Registration failed');
      setLoading(false);
      throw err;
    }
  };

  const forgotPassword = async (data: ForgotPasswordRequest): Promise<void> => {
    try {
      setError(null);
      await apiForgotPassword(data);
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message || 'Failed to send password reset email');
      throw err;
    }
  };

  const resetPassword = async (data: ResetPasswordRequest): Promise<void> => {
    try {
      setError(null);
      await apiResetPassword(data);
      // Redirect to login page after successful password reset
      window.location.href = '/login';
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message || 'Password reset failed');
      throw err;
    }
  };

  const logout = async () => {
    try {
      setIsLoggingOut(true);
      await apiLogout();
      // Clear all authentication-related storage
      setUser(null);
      clearUserAccessToken();
      localStorage.removeItem('authToken'); // Clear JWT token
      sessionStorage.removeItem('redirectAfterLogin');
      // Redirect to login page
      window.location.href = '/login';
    } catch (err) {
      console.error('Logout error:', err);
      // Clear local state even if API call fails
      setUser(null);
      clearUserAccessToken();
      sessionStorage.removeItem('redirectAfterLogin');
      // Redirect to login page even on error
      window.location.href = '/login';
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    forgotPassword,
    resetPassword,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

